// src/components/PushPermissionPrompt.jsx
// Shows once after login to ask for push notification permission.
// Stores dismissal in localStorage so it only shows once per browser.

import React, { useState, useEffect } from 'react';
import { requestPushPermission } from '../firebase-utils';

const DISMISSED_KEY = 'GigWave_pushPromptDismissed';

export default function PushPermissionPrompt({ userId }) {
  const [show, setShow] = useState(false);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    if (!userId) return;
    // Don't show if already dismissed, already granted, or not supported
    if (!('Notification' in window)) return;
    if (Notification.permission === 'granted') return;
    if (Notification.permission === 'denied') return;
    if (localStorage.getItem(DISMISSED_KEY)) return;

    // Small delay so it doesn't flash immediately on login
    const t = setTimeout(() => setShow(true), 3000);
    return () => clearTimeout(t);
  }, [userId]);

  const handleAllow = async () => {
    setLoading(true);
    try {
      const token = await requestPushPermission(userId);
      if (token) {
        setDone(true);
        setTimeout(() => setShow(false), 2000);
      } else {
        // Permission denied by browser dialog
        localStorage.setItem(DISMISSED_KEY, '1');
        setShow(false);
      }
    } catch (err) {
      console.warn('Push setup error:', err);
      setShow(false);
    } finally {
      setLoading(false);
    }
  };

  const handleDismiss = () => {
    localStorage.setItem(DISMISSED_KEY, '1');
    setShow(false);
  };

  if (!show) return null;

  return (
    <div style={{
      position: 'fixed',
      bottom: '24px',
      left: '50%',
      transform: 'translateX(-50%)',
      zIndex: 9999,
      width: 'calc(100% - 32px)',
      maxWidth: '420px',
      background: 'rgba(10, 10, 25, 0.97)',
      border: '1px solid rgba(168, 85, 247, 0.5)',
      borderRadius: '16px',
      padding: '16px 20px',
      boxShadow: '0 8px 32px rgba(0,0,0,0.6)',
      animation: 'slideUp 0.3s ease',
    }}>
      <style>{`
        @keyframes slideUp {
          from { transform: translateX(-50%) translateY(20px); opacity: 0; }
          to   { transform: translateX(-50%) translateY(0);  opacity: 1; }
        }
      `}</style>

      {done ? (
        <div className="text-center py-2">
          <p className="text-2xl mb-1">✅</p>
          <p style={{ color: '#22c55e', fontWeight: 'bold', fontSize: '15px' }}>
            You're all set! We'll notify you when artists go live.
          </p>
        </div>
      ) : (
        <>
          <div style={{ display: 'flex', gap: '12px', alignItems: 'flex-start', marginBottom: '14px' }}>
            <span style={{ fontSize: '28px', flexShrink: 0 }}>🔔</span>
            <div>
              <p style={{ color: 'white', fontWeight: 'bold', fontSize: '15px', marginBottom: '4px' }}>
                Never miss a live gig
              </p>
              <p style={{ color: '#9ca3af', fontSize: '13px', lineHeight: '1.5' }}>
                Get notified when artists you follow go live or schedule new gigs — even when this tab is closed.
              </p>
            </div>
          </div>
          <div style={{ display: 'flex', gap: '10px' }}>
            <button
              onClick={handleAllow}
              disabled={loading}
              style={{
                flex: 1,
                padding: '10px',
                borderRadius: '50px',
                fontWeight: 'bold',
                fontSize: '14px',
                cursor: loading ? 'not-allowed' : 'pointer',
                background: loading ? '#333' : 'linear-gradient(135deg, #7c3aed, #a855f7)',
                color: 'white',
                border: 'none',
                opacity: loading ? 0.7 : 1,
                transition: 'all 0.2s',
              }}>
              {loading ? '⏳ Setting up...' : '🔔 Allow notifications'}
            </button>
            <button
              onClick={handleDismiss}
              style={{
                padding: '10px 16px',
                borderRadius: '50px',
                fontSize: '13px',
                cursor: 'pointer',
                background: 'rgba(255,255,255,0.07)',
                color: '#9ca3af',
                border: '1px solid rgba(255,255,255,0.15)',
              }}>
              Not now
            </button>
          </div>
        </>
      )}
    </div>
  );
}
